<template>
  <SectionHeader
    :show-content="dynamicInboundCallsSettingsShowContents"
    :widget-title="$t('HEADER_DYNAMIC_INBOUND_CALLS')"
    @onShowContentsClick="onDynamicInboundCallsSettingsShowContentsClick"
  >
    <template #content>
      <form
        role="form"
        class="form-horizontal"
        name="dynamicInboundCallsSettings"
      >
        <div v-if="affiliateView && !isPublisherGroupUser" class="p-t-2 p-b-10">
          <Field
            :label="$t('LABEL_ID')"
            :hide-label="false"
            :has-tooltip="true"
            :tooltip-text="$t('TOOLTIP_ID')"
          >
            <template #element>
              <div class="col-sm-6 col-md-4 p-t-2 p-b-10">
                <p>
                  <a
                    v-clipboard="{ copyText: affiliateViewRtbId }"
                    class="btn btn-link p-l-0 p-r-5"
                  >
                    <i
                      v-tooltip.right="$t('TOOLTIP_COPY_BUTTON_ID')"
                      class="typcn typcn-tabs-outline"
                      aria-hidden="true"
                    />
                  </a>
                  {{ affiliateViewRtbId }}
                </p>
              </div>
            </template>
          </Field>

          <Field
            v-if="enabled"
            :label="$t('LABEL_DOCUMENTATION')"
            :hide-label="false"
            :has-tooltip="true"
            :tooltip-text="$t('TOOLTIP_DOCUMENTATION')"
          >
            <template #element>
              <div class="col-sm-6 col-md-4 p-t-2 p-b-10">
                <button
                  type="button"
                  data-cy="button-view-documentation"
                  class="btn btn-primary"
                  @click="openDynamicInboundCallsDocumentationModal"
                >
                  <span>{{ $t('TOOLTIP_DOCUMENTATION') }}</span>
                </button>
                <DocumentationModal
                  v-if="affiliate.showDynamicInboundCallsDocumentationModal"
                  :campaign-id="campaignId"
                  :affiliate="affiliate"
                  :rtb-id="affiliateViewRtbId"
                  :on-close-modal="closeDynamicInboundCallsDocumentationModal"
                />
              </div>
            </template>
          </Field>
        </div>

        <div v-if="!overridesAvailable || overrideDynamicInboundCallsSettings">
          <Field
            :label="$t('LABEL_ENABLED')"
            :hide-label="false"
            :has-tooltip="true"
            :tooltip-text="$t('TOOLTIP_ENABLED')"
          >
            <template #element>
              <ValidationProvider rules="required" slim>
                <div class="col-xs-3 col-sm-2 col-md-2 col-lg-2 toggle-wrap">
                  <nzToggle
                    v-model="enabled"
                    :disabled="affiliateView"
                    data-cy="rtb-enabled-toggle"
                    @onToggle="enabledToggle"
                  />
                </div>
              </ValidationProvider>
            </template>
          </Field>
        </div>

        <div v-if="enabled && !overridesAvailable">
          <Field
            :label="$t('LABEL_ONLY_SIP')"
            :hide-label="false"
            :has-tooltip="true"
            :tooltip-text="$t('TOOLTIP_ONLY_SIP')"
          >
            <template #element>
              <ValidationProvider rules="required" slim>
                <div class="col-xs-3 col-sm-2 col-md-2 col-lg-2 toggle-wrap">
                  <nzToggle
                    v-model="sipOnly"
                    :disabled="affiliateView"
                    @onToggle="onlySIPToggle"
                  />
                </div>
              </ValidationProvider>
            </template>
          </Field>
        </div>

        <Field
          v-if="enabled"
          :label="$t('LABEL_REQUIRE_CALLER_ID')"
          :hide-label="false"
          :has-tooltip="true"
          :tooltip-text="$t('TOOLTIP_REQUIRE_CALLER_ID')"
        >
          <template #element>
            <ValidationProvider rules="required" slim>
              <div class="col-xs-3 col-sm-2 col-md-2 col-lg-2 toggle-wrap">
                <nzToggle
                  v-model="callerIdRequired"
                  :disabled="affiliateView"
                  @onToggle="callerIdRequiredToggle"
                />
              </div>
            </ValidationProvider>
          </template>
        </Field>

        <PayoutSettings
          v-if="showPayoutSettings && enabled"
          :enableCapsLabel="$t('LIMIT_RTB_PAYOUT_LABEL')"
          :enableCapsTooltip="$t('TOOLTIP_LIMIT_PAYOUT')"
          :translactionDictionary="publisherPayoutCapFieldsTranslaction"
          :payout-capacity="payoutCapacity"
          :call-caps="callCaps"
          :affiliate-view="affiliateView"
          @change-call-caps="onCallCapsChange"
          @change-publisher-payout-capacity="onPayoutCapsChange"
        />

        <div v-if="isIcpVisible">
          <Field
            :label="$t('LABEL_ICP')"
            :hide-label="false"
            :has-tooltip="true"
            :tooltip-text="$t('TOOLTIP_ICP')"
          >
            <template #element>
              <ValidationProvider rules="required" slim>
                <MultipleToggle
                  :value="icp"
                  :options="icpOptions"
                  :disabled="affiliateView"
                  @onToggle="onIcpChange"
                />
              </ValidationProvider>
            </template>
          </Field>
        </div>

        <div v-if="isJornayaVisible">
          <Field
            :label="$t('LABEL_JORNAYA_TCPA')"
            :hide-label="false"
            :has-tooltip="true"
            :tooltip-text="$t('TOOLTIP_JORNAYA_TCPA')"
          >
            <template #element>
              <ValidationProvider rules="required" slim>
                <MultipleToggle
                  :value="journayaTcpa"
                  :options="journayaTcpaOptions"
                  :disabled="affiliateView"
                  @onToggle="onJournayaTcpaChange"
                />
              </ValidationProvider>
            </template>
          </Field>
          <Field
            v-if="journayaTcpa != 'Off'"
            :label="$t('LABEL_JORNAYA_CREDENTIALS')"
            :hide-label="false"
            :has-tooltip="true"
            :tooltip-text="$t('TOOLTIP_JORNAYA_CREDENTIALS')"
          >
            <template #element>
              <ValidationProvider rules="required" slim>
                <MultipleToggleOverride
                  toggle-name="jornayaSettings"
                  :value="overrideJornayaSettings"
                  :options="jornayaOverrideOptions"
                  :disabled="affiliateView"
                  @onToggle="overrideJornayaSettingsToggle"
                />
              </ValidationProvider>
            </template>
          </Field>
          <div v-if="journayaTcpa != 'Off' && isJornayaSettingsVisible">
            <JornayaSettings
              :jornaya-setting="jornayaSettings"
              @jornayaSettingsChange="onJornayaSettingsChange"
            />
          </div>
        </div>

        <div v-if="isTrustedFormVisible">
          <Field
            :label="$t('LABEL_TRUSTED_FORM')"
            :hide-label="false"
            :has-tooltip="true"
            :tooltip-text="$t('TOOLTIP_TRUSTED_FORM')"
          >
            <template #element>
              <ValidationProvider rules="required" slim>
                <MultipleToggle
                  :value="trustedForm"
                  :options="trustedFormOptions"
                  :disabled="affiliateView"
                  @onToggle="onTrustedFormChange"
                />
              </ValidationProvider>
            </template>
          </Field>
          <Field
            v-if="trustedForm != 'Off'"
            :label="$t('LABEL_TRUSTED_FORM_METHOD')"
            :hide-label="false"
            :has-tooltip="true"
            :tooltip-text="$t('TOOLTIP_TRUSTED_FORM_METHOD')"
          >
            <template #element>
              <ValidationProvider rules="required" slim>
                <MultipleToggleOverride
                  :value="trustedFormIntegration.integrationMethod"
                  :options="trustedFormIntegrationOptions"
                  :disabled="affiliateView"
                  @onToggle="onTrustedFormIntegrationChange"
                />
              </ValidationProvider>
            </template>
          </Field>
          <Field
            v-if="trustedForm != 'Off'"
            :label="$t('LABEL_OVERRIDE_TRUSTED_FORM')"
            :hide-label="false"
            :has-tooltip="true"
            :tooltip-text="$t('TOOLTIP_OVERRIDE_TRUSTED_FORM')"
          >
            <template #element>
              <ValidationProvider rules="required" slim>
                <MultipleToggleOverride
                  toggle-name="trustedFormSettings"
                  :value="overrideTrustedFormSettings"
                  :options="trustedFormOverrideOptions"
                  :disabled="affiliateView"
                  @onToggle="overrideTrustedFormSettingsToggle"
                />
              </ValidationProvider>
            </template>
          </Field>
          <div v-if="trustedForm != 'Off' && isTrustedFormSettingsVisible">
            <TrustedFormSettings
              :trusted-form-setting="trustedFormSettings"
              @trustedFormSettingsChange="onTrustedFormSettingsChange"
            />
          </div>
        </div>
      </form>
      <IcpConfirmationModal
        v-if="showIcpConfirmationModal"
        :is-loading-data="isLoadingPricingPlan"
        :pricing-plan="pricingPlan"
        @onConfirmIcpPricing="onConfirmIcpPricing"
      />
    </template>
  </SectionHeader>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { STATUS_ERROR, STATUS_PENDING } from '@/store/helpers';
import { utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';

import messages from '../locales';
import sharedMessages from '@/core/locales';

import SectionHeader from '@/core/components/SectionHeader/SectionHeader';
import Field from '@/core/components/LabelAndElement/LabelAndElement';
import nzToggle from '@/core/components/nzToggle/nzToggle';
import MultipleToggle from '@/core/components/MultipleToggle/MultipleToggle';
import MultipleToggleOverride from '@/core/components/MultipleToggleOverride/MultipleToggleOverride';
import DocumentationModal from '@/core/components/DocumentationModal/DocumentationModal';
import TrustedFormSettings from '@/core/components/TrustedFormSettings';
import JornayaSettings from '@/core/components/JornayaSettings';
import PayoutSettings from '@/core/components/PayoutSettings/PayoutSettings';
import IcpConfirmationModal from '../IcpConfirmationModal/IcpConfirmationModal';

export default {
  name: 'DynamicInboundCallsSettings',
  components: {
    SectionHeader,
    Field,
    nzToggle,
    MultipleToggle,
    MultipleToggleOverride,
    DocumentationModal,
    TrustedFormSettings,
    JornayaSettings,
    PayoutSettings,
    IcpConfirmationModal,
  },
  props: {
    campaignId: {
      type: String,
      default: () => {
        return '';
      },
    },
    affiliateViewRtbId: {
      type: String,
      default: () => {
        return '';
      },
    },
    enabled: {
      type: Boolean,
      default: () => {
        return false;
      },
    },
    sipOnly: {
      type: Boolean,
      default: () => {
        return true;
      },
    },
    callerIdRequired: {
      type: Boolean,
      default: () => {
        return true;
      },
    },
    icp: {
      type: String,
      default: () => {
        return [];
      },
    },
    journayaTcpa: {
      type: String,
      default: () => {
        return [];
      },
    },
    jornayaSettings: {
      type: Object,
      default() {
        return {};
      },
    },
    trustedForm: {
      type: String,
      default: () => {
        return [];
      },
    },
    trustedFormIntegration: {
      type: Object,
      default: () => {
        return {};
      },
    },
    trustedFormSettings: {
      type: Object,
      default() {
        return {};
      },
    },
    overridesAvailable: {
      type: Boolean,
      default: () => {
        return false;
      },
    },
    overrideDynamicInboundCallsSettings: {
      type: Boolean,
      default: () => {
        return false;
      },
    },
    affiliate: {
      type: Object,
      default() {
        return {};
      },
    },
    affiliateView: {
      type: Boolean,
      default: () => {
        return false;
      },
    },
    payoutCapacity: {
      type: Object,
      default() {
        return {};
      },
    },
    callCaps: {
      type: Object,
      default() {
        return {};
      },
    },
    campaginIdAndAffiliateId: {
      type: String,
      default() {
        return '';
      },
    },
  },
  data() {
    return {
      dynamicInboundCallsSettingsShowContents: true,
      icpOptions: Object.freeze([
        { name: this.$t('MULTIPLE_TOGGLE_OPTIONS_DISABLED'), value: 'Off' },
        { name: this.$t('MULTIPLE_TOGGLE_OPTIONS_ENABLED'), value: 'On' },
        {
          name: this.$t('MULTIPLE_TOGGLE_OPTIONS_REQUIRED'),
          value: 'Required',
        },
      ]),
      journayaTcpaOptions: Object.freeze([
        { name: this.$t('MULTIPLE_TOGGLE_OPTIONS_DISABLED'), value: 'Off' },
        { name: this.$t('MULTIPLE_TOGGLE_OPTIONS_ENABLED'), value: 'On' },
        {
          name: this.$t('MULTIPLE_TOGGLE_OPTIONS_REQUIRED'),
          value: 'Required',
        },
      ]),
      trustedFormOptions: Object.freeze([
        { name: this.$t('MULTIPLE_TOGGLE_OPTIONS_DISABLED'), value: 'Off' },
        { name: this.$t('MULTIPLE_TOGGLE_OPTIONS_ENABLED'), value: 'On' },
        {
          name: this.$t('MULTIPLE_TOGGLE_OPTIONS_REQUIRED'),
          value: 'Required',
        },
      ]),
      trustedFormIntegrationOptions: Object.freeze([
        { name: this.$t('MULTIPLE_TOGGLE_OPTIONS_CLAIM'), value: 'Claim' },
        { name: this.$t('MULTIPLE_TOGGLE_OPTIONS_VERIFY'), value: 'Ping' },
      ]),
      trustedFormOverrideOptions: [
        {
          name: this.$t('ACCOUNT_SETTINGS'),
          state: {
            text: '',
            link: '/#/dashboard/user-account-settings',
          },
          value: false,
        },
        {
          name: this.$t('OVERRIDE'),
          value: true,
        },
      ],
      jornayaOverrideOptions: [
        {
          name: this.$t('ACCOUNT_SETTINGS'),
          state: {
            text: '',
            link: '/#/dashboard/user-account-settings',
          },
          value: false,
        },
        {
          name: this.$t('OVERRIDE'),
          value: true,
        },
      ],
      overrideTrustedFormSettings: false,
      defaultTrustedFormSettings: {
        apiKey: null,
        certificateTagName: null,
      },
      overrideJornayaSettings: false,
      defaultJornayaSettings: {
        accountCode: null,
        auditKey: null,
        agencyCode: null,
      },
      showPayoutSettings: false,
      showIcpConfirmationModal: false,
      previousIcpOption: ''
    };
  },
  created() {
    this.trustedFormOverrideOptions[0].state.text =
      this.trustedFormAccountSettingsState;
    this.overrideTrustedFormSettings =
      this.trustedFormSettings?.apiKey &&
      this.trustedFormSettings?.certificateTagName
        ? true
        : false;
    this.jornayaOverrideOptions[0].state.text =
      this.jornayaAccountSettingsState;
    this.overrideJornayaSettings =
      this.jornayaSettings?.accountCode &&
      this.jornayaSettings?.auditKey &&
      this.jornayaSettings?.agencyCode
        ? true
        : false;
  },
  watch: {
    trustedFormAccountSettingsState() {
      this.trustedFormOverrideOptions[0].state.text =
        this.trustedFormAccountSettingsState;
    },
    jornayaAccountSettingsState() {
      this.jornayaOverrideOptions[0].state.text =
        this.jornayaAccountSettingsState;
    },
    campaginIdAndAffiliateId: {
      handler(newVal) {
        if (newVal === this.campaignId) {
          this.showPayoutSettings = false;
        } else {
          this.showPayoutSettings = true;
        }
      },
      immediate: true,
      deep: true,
    },
  },
  computed: {
    ...mapState(['currentAccountSettings']),

    ...mapState('billingSettingsStore', ['pricingPlan']),

    ...mapGetters([
      'icpFeatureFlag',
      'isPublisherGroupUser',
    ]),

    ...mapGetters('billingSettingsStore', ['getPricingPlanStatus']),

    isTrustedFormVisible() {
      return (
        this.enabled &&
        !this.affiliateView &&
        !this.overridesAvailable
      );
    },
    isTrustedFormSettingsVisible() {
      return (
        this.overrideTrustedFormSettings ||
        (this.trustedFormSettings?.apiKey &&
          this.trustedFormSettings?.certificateTagName)
      );
    },
    trustedFormAccountSettingsState() {
      return this.currentAccountSettings?.trustedFormSettings?.apiKey &&
        this.currentAccountSettings?.trustedFormSettings?.certificateTagName
        ? 'Configured'
        : 'Not Configured';
    },
    isIcpVisible() {
      return (
        this.enabled &&
        !this.affiliateView &&
        !this.overridesAvailable &&
        this.icpFeatureFlag
      );
    },
    isJornayaVisible() {
      return (
        this.enabled &&
        !this.affiliateView &&
        !this.overridesAvailable
      );
    },
    isJornayaSettingsVisible() {
      return (
        this.overrideJornayaSettings ||
        (this.jornayaSettings?.accountCode &&
          this.jornayaSettings?.auditKey &&
          this.jornayaSettings?.agencyCode)
      );
    },
    jornayaAccountSettingsState() {
      return this.currentAccountSettings?.jornayaSettings?.accountCode &&
        this.currentAccountSettings?.jornayaSettings?.auditKey &&
        this.currentAccountSettings?.jornayaSettings?.agencyCode
        ? 'Configured'
        : 'Not Configured';
    },
    publisherPayoutCapFieldsTranslaction() {
      return {
        capCategory: 'rtb',
        capCriteria: 'Payouts',
      };
    },
    isLoadingPricingPlan() {
      return this.getPricingPlanStatus.status === STATUS_PENDING;
    }
  },
  methods: {
    ...mapActions('billingSettingsStore', ['getPricingPlan']),
    openDynamicInboundCallsDocumentationModal() {
      this.affiliate.showDynamicInboundCallsDocumentationModal = true;
    },
    closeDynamicInboundCallsDocumentationModal() {
      this.affiliate.showDynamicInboundCallsDocumentationModal = false;
    },
    overrideDynamicInboundCallsSettingsToggle() {
      this.$emit('overrideDynamicInboundCallsSettingsToggle');
    },
    enabledToggle(val) {
      this.$emit('onEnabledToggle', val);
    },
    onlySIPToggle(val) {
      this.$emit('onOnlySipToggle', val);
    },
    callerIdRequiredToggle(val) {
      this.$emit('onCallerIdRequiredToggle', val);
    },
    async onIcpChange(val) {
      if (val.value === this.icp) {
        return;
      }
      this.previousIcpOption = this.icp;
      this.$emit('onIcpChange', val); // emit the change in value so multi-toggle registers the change

      if (val.value !== 'Off') {
        this.showIcpConfirmationModal = true;

        await this.getPricingPlan(); // We are deciding to fetch the plan data every time, but the store is implemented for possible future use

        if (this.getPricingPlanStatus.status === STATUS_ERROR) {
          // If the request to get pricing errors out, throw error toaster, reset option to previous option, and close modal
          utilities.showToaster(STATUS_ERROR, this.getPricingPlanStatus.error);
          const icp = this.icpOptions.find(option => option.value === this.previousIcpOption);
          this.$emit('onIcpChange', icp);
          this.showIcpConfirmationModal = false;
        }
      }
    },
    onConfirmIcpPricing(confirm) {
      // If the pricing was not confirmed, val will be false, revert to the previous setting and close modal
      if (!confirm) {
        if (!this.previousIcpOption) this.previousIcpOption = 'Off';
        const icp = this.icpOptions.find(option => option.value === this.previousIcpOption);
        this.$emit('onIcpChange', icp);
      }
      this.showIcpConfirmationModal = false;
    },
    onPayoutCapsChange(val) {
      this.$emit('onPayoutCapsChange', val);
    },
    onCallCapsChange(val) {
      this.$emit('onCallCapsChange', val);
    },
    onJournayaTcpaChange(val) {
      if (val.value === 'Off') {
        this.overrideJornayaSettings = false;
        this.$emit('onJornayaSettingsChange', this.defaultJornayaSettings);
      }
      this.$emit('onJournayaTcpaChange', val);
    },
    overrideJornayaSettingsToggle(option) {
      if (!option.value) {
        this.$emit('onJornayaSettingsChange', this.defaultJornayaSettings);
      }
      this.overrideJornayaSettings = option.value;
    },
    onJornayaSettingsChange(val) {
      this.$emit('onJornayaSettingsChange', val);
    },
    onTrustedFormChange(val) {
      if (val.value === 'Off') {
        this.overrideTrustedFormSettings = false;
        this.$emit(
          'onTrustedFormSettingsChange',
          this.defaultTrustedFormSettings
        );
      }
      this.$emit('onTrustedFormChange', val);
    },
    onTrustedFormIntegrationChange(val) {
      this.$emit('onTrustedFormIntegrationChange', val);
    },
    overrideTrustedFormSettingsToggle(option) {
      if (!option.value) {
        this.$emit(
          'onTrustedFormSettingsChange',
          this.defaultTrustedFormSettings
        );
      }
      this.overrideTrustedFormSettings = option.value;
    },
    onTrustedFormSettingsChange(val) {
      this.$emit('onTrustedFormSettingsChange', val);
    },
    onDynamicInboundCallsSettingsShowContentsClick() {
      this.dynamicInboundCallsSettingsShowContents =
        !this.dynamicInboundCallsSettingsShowContents;
    },
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en',
  },
};
</script>